// @flow
import some from 'lodash/some'
import values from 'lodash/values'
import { invalidateLoadCustomers } from 'queries/customers/useLoadCustomer'
import * as React from 'react'
import { toast } from 'react-toastify'
import TextField from 'ui/molecules/TextField'
import { buildFullName } from 'utils/helpers'
import {
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  schemasValidate,
} from 'utils/schemas'

import api from '../api'
import Ui from '../ui'

type Props = {
  onHide: () => void,
}

export default function CreateClient({ onHide }: Props): React.Node {
  const initialFormError = React.useRef({
    first: false,
    last: false,
    email: false,
  }).current
  const initialFormErrorLabel = React.useRef({
    first: undefined,
    last: undefined,
    email: undefined,
  }).current

  const [email, setEmail] = React.useState('')
  const [first, setFirstName] = React.useState('')
  const [last, setLastName] = React.useState('')

  const [hasFormError, setHasFormError] = React.useState(false)
  const [formError, setFormError] = React.useState(initialFormError)
  const [formErrorLabel, setFormErrorLabel] = React.useState(
    initialFormErrorLabel
  )
  const [isSubmitting, setSubmitting] = React.useState(false)

  const createClient = async (e: any) => {
    e.preventDefault()
    try {
      setSubmitting(true)
      const isFormValid = await validateForm()
      if (!isFormValid) return false

      await api.createClient(first, last, email)
      toast.info(`${buildFullName(first, last)} created`)
      invalidateLoadCustomers()
      handleOut()
    } finally {
      setSubmitting(false)
    }
  }

  const handleOut = () => {
    setSubmitting(false)
    onHide()
  }

  // FORM VALIDATOR
  React.useEffect(() => {
    setHasFormError((p) => {
      const n = some(values(formError), (err) => err === true)
      return n !== p ? n : p
    })
  }, [formError])

  function validateForm() {
    return schemasValidate(
      [
        ['first', first, firstNameSchema],
        ['last', last, lastNameSchema],
        ['email', email, emailSchema],
      ],
      setFormError,
      setFormErrorLabel
    )
  }

  const validateEmail = React.useCallback(() => {
    schemasValidate(
      [['email', email, emailSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [email])

  React.useEffect(() => {
    formError?.email === true && validateEmail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email])

  const validateFirstName = React.useCallback(() => {
    schemasValidate(
      [['first', first, firstNameSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [first])

  React.useEffect(() => {
    formError?.first === true && validateFirstName()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first])

  const validateLastName = React.useCallback(() => {
    schemasValidate(
      [['last', last, lastNameSchema]],
      setFormError,
      setFormErrorLabel
    )
  }, [last])

  React.useEffect(() => {
    formError?.last === true && validateLastName()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [last])

  return (
    <Ui.ModalDialog opened onClose={handleOut} title="Client Information">
      <div onClick={(e) => e.stopPropagation()}>
        <form>
          <div>
            <div>
              <div>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Use an email they have access to
                </p>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <TextField
                    errorLabel={formErrorLabel?.first}
                    hasError={formError?.first}
                    label="First Name"
                    name="first_name"
                    onBlur={validateFirstName}
                    onChange={setFirstName}
                    value={first}
                  />
                </div>

                <div className="sm:col-span-3">
                  <TextField
                    errorLabel={formErrorLabel?.last}
                    hasError={formError?.last}
                    label="Last Name"
                    name="last_name"
                    onBlur={validateLastName}
                    onChange={setLastName}
                    value={last}
                  />
                </div>

                <div className="sm:col-span-6">
                  <TextField
                    errorLabel={formErrorLabel?.email}
                    hasError={formError?.email}
                    label="Email address"
                    name="email"
                    onBlur={validateEmail}
                    onChange={setEmail}
                    type="email"
                    value={email}
                  />
                </div>

                <div className="sm:col-span-6">
                  <div>
                    <Ui.PrimaryButton
                      disabled={hasFormError || isSubmitting}
                      onClick={createClient}
                    >
                      Create Client
                    </Ui.PrimaryButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Ui.ModalDialog>
  )
}
